import { memo } from 'react'
import { cn } from '@/utils'
import type { Message } from '@/stores'
import { InlineMessageFeedback, type MessageFeedbackData } from '@/components/feedback'
import { User, Bot, AlertCircle, Paperclip } from 'lucide-react'
import { RenderedMessage } from '../RenderedMessage'
import { MediaAttachment } from '@/components/inbox/MediaAttachment'
import { CONTINUE_LABEL, cutLine } from '@/utils/replyCutText'
import { workflowReplyLine } from '@/utils/workflowReplyText'
import {
  Button,
  ChatBubble,
  ChatBubbleAvatar,
  ChatBubbleContent,
  ChatBubbleMessage,
  ChatBubbleMeta,
} from '@/components/ui'

interface ExperienceMessageBubbleProps {
  message: Message
  isStreaming?: boolean
  onFeedback?: (data: MessageFeedbackData) => void
  showFeedback?: boolean
  /** Ask for the rest of a cut reply (SA-1241); the button shows only when given. */
  onContinue?: () => void
}

/** Strip LLM reasoning blocks before rendering */
function stripThinkBlocks(content: string): string {
  return content
    .replace(/<think>[\s\S]*?<\/think>/g, '')
    .replace(/^[\s\S]*?<\/think>\s*/g, '')
    .trim()
}

function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()

  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const ExperienceMessageBubble = memo(function ExperienceMessageBubble({
  message,
  isStreaming = false,
  onFeedback,
  showFeedback = true,
  onContinue,
}: ExperienceMessageBubbleProps) {
  const isUser = message.role === 'user'
  const isAssistant = message.role === 'assistant'
  const hasError = !!message.error
  const isEmptyStreaming = isStreaming && isAssistant && !message.content
  const cut = isAssistant && !isStreaming && !hasError ? message.cut : undefined
  const workflow = isAssistant && !isStreaming && !hasError ? message.workflow : undefined

  return (
    <ChatBubble variant={isUser ? 'sent' : 'received'} className="group px-4 py-2.5">
      <ChatBubbleAvatar tone={isUser ? 'muted' : 'primary'}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </ChatBubbleAvatar>

      <ChatBubbleContent>
        <ChatBubbleMessage
          loading={isEmptyStreaming}
          className={cn(
            hasError && 'border border-destructive/50 bg-destructive/10 text-destructive',
          )}
        >
          {message.mediaType && message.mediaUrl && (
            <div className="mb-2">
              <MediaAttachment
                messageId={message.id}
                mediaType={message.mediaType}
                mediaUrl={message.mediaUrl}
              />
            </div>
          )}
          {message.mediaType && !message.mediaUrl && message.attachment && (
            <div
              data-testid="attachment-pending-chip"
              className="mb-2 flex items-center gap-2 text-sm opacity-80"
            >
              <Paperclip className="w-4 h-4 shrink-0" />
              <span className="truncate">{message.attachment.filename}</span>
            </div>
          )}
          {hasError ? (
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="text-sm">{message.error}</span>
            </div>
          ) : isUser ? (
            <span className="whitespace-pre-wrap">{message.content}</span>
          ) : (
            <RenderedMessage content={stripThinkBlocks(message.content)} />
          )}
        </ChatBubbleMessage>

        {cut && (
          <div data-testid="reply-cut" className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
            <span>{cutLine(cut.reason)}</span>
            {onContinue && (
              <Button type="button" variant="secondary" size="sm" className="h-6 px-2 text-xs" onClick={onContinue}>
                {CONTINUE_LABEL}
              </Button>
            )}
          </div>
        )}

        {workflow && (
          <div data-testid="reply-workflow" className="px-1 text-xs text-muted-foreground">
            {workflowReplyLine(workflow.name)}
          </div>
        )}

        <ChatBubbleMeta>
          <span>{formatTimestamp(message.timestamp)}</span>
          {isAssistant && showFeedback && !isStreaming && !hasError && onFeedback && (
            <InlineMessageFeedback messageId={message.id} onFeedback={onFeedback} />
          )}
        </ChatBubbleMeta>
      </ChatBubbleContent>
    </ChatBubble>
  )
})
